import { Body, Button, IconButton, Radio } from '@assembly-js/design-system';
import { Popover } from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { TextField } from 'src/components/TextField';
import { MultiSelectOption } from 'src/legacy/components/Select/types';

// Long lists get a search box, as in the design.
const SEARCH_THRESHOLD = 5;

export interface CustomFieldSelectCopy {
  chooseOne: string;
  addOption: string;
  newOptionPlaceholder: string;
  searchPlaceholder: string;
  noOptions: string;
  noMatches: string;
  rename: string;
  remove: string;
  cancel: string;
  clear: string;
  save: string;
  nameRequired: string;
  nameTaken: string;
}

interface NameRowProps {
  initial: string;
  placeholder: string;
  copy: CustomFieldSelectCopy;
  taken: (label: string) => boolean;
  onSave: (label: string) => void;
  onCancel: () => void;
  className?: string;
}

/** A name box with confirm and cancel, for adding or renaming an option. */
const NameRow = ({
  initial,
  placeholder,
  copy,
  taken,
  onSave,
  onCancel,
  className,
}: NameRowProps) => {
  const [label, setLabel] = useState(initial);
  const [error, setError] = useState('');

  const save = () => {
    const trimmed = label.trim();
    if (!trimmed) return setError(copy.nameRequired);
    if (taken(trimmed)) return setError(copy.nameTaken);
    onSave(trimmed);
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-1">
        <TextField
          autoFocus
          fullWidth
          value={label}
          placeholder={placeholder}
          error={Boolean(error)}
          inputProps={{ 'aria-label': placeholder }}
          onChange={(e) => {
            setLabel(e.target.value);
            setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') {
              // Escape leaves the name box, not the picker.
              e.stopPropagation();
              onCancel();
            }
          }}
          className="min-w-0 flex-1"
        />
        <IconButton
          icon="Check"
          size="sm"
          variant="primary"
          label={copy.save}
          onClick={save}
        />
        <IconButton
          icon="Close"
          size="sm"
          variant="minimal"
          label={copy.cancel}
          onClick={onCancel}
        />
      </div>
      {error && <ErrorMessage message={error} />}
    </div>
  );
};

interface Props {
  anchorEl: HTMLElement | null;
  fieldName: string;
  options: MultiSelectOption[];
  selectedId?: string;
  onSelect: (optionId: string) => void;
  /** Empties the record's value; offered while an option is chosen. */
  onClear: () => void;
  onAddOption?: (label: string) => void;
  onRenameOption?: (optionId: string, label: string) => void;
  onRemoveOption?: (optionId: string) => void;
  onClose: () => void;
  // Copy comes from the call site.
  copy: CustomFieldSelectCopy;
}

/** The one-choice picker for a select field: radio rows, rename and remove on hover, and add at the bottom. */
export const CustomFieldSelectPopover = ({
  anchorEl,
  fieldName,
  options,
  selectedId,
  onSelect,
  onClear,
  onAddOption,
  onRenameOption,
  onRemoveOption,
  onClose,
  copy,
}: Props) => {
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);

  const reset = () => {
    setQuery('');
    setAdding(false);
    setRenamingId(null);
  };
  const close = () => {
    reset();
    onClose();
  };
  const taken = (label: string, exceptId?: string) =>
    options.some(
      (option) =>
        option.id !== exceptId &&
        option.label.toLowerCase() === label.toLowerCase(),
    );

  const needle = query.trim().toLowerCase();
  const visible = options.filter(
    (option) => !needle || option.label.toLowerCase().includes(needle),
  );

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          className:
            'mt-1 box-border min-w-[240px] max-w-[340px] rounded-[6px] border border-solid border-gray-150 shadow-[0_9px_24px_rgba(0,0,0,0.12)]',
          onKeyDown: (e: React.KeyboardEvent) => {
            // Escape backs out of a name box before closing.
            if (e.key === 'Escape' && (adding || renamingId)) {
              e.stopPropagation();
              setAdding(false);
              setRenamingId(null);
            }
          },
        },
      }}
    >
      <div className="flex items-center justify-between gap-3 px-3 pt-2.5 pb-1.5">
        <Body
          size="xs"
          tag="span"
          className="truncate font-medium leading-[18px] text-gray-small"
        >
          {fieldName}
        </Body>
        <Body
          size="xs"
          tag="span"
          className="shrink-0 leading-[18px] text-light-gray"
        >
          {copy.chooseOne}
        </Body>
      </div>

      {options.length > SEARCH_THRESHOLD && (
        <div className="border-0 border-b border-solid border-gray-150 px-2 py-1.5">
          <TextField
            autoFocus={!adding}
            fullWidth
            value={query}
            placeholder={copy.searchPlaceholder}
            inputProps={{ 'aria-label': copy.searchPlaceholder }}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}

      <div className="grid p-1">
        {visible.length === 0 && (
          <Body size="sm" tag="div" className="p-2 leading-5 text-light-gray">
            {needle ? copy.noMatches : copy.noOptions}
          </Body>
        )}
        {visible.map((option) => {
          if (renamingId === option.id && onRenameOption) {
            return (
              <NameRow
                key={option.id}
                initial={option.label}
                placeholder={copy.rename}
                copy={copy}
                taken={(label) => taken(label, option.id)}
                onSave={(label) => {
                  onRenameOption(option.id, label);
                  setRenamingId(null);
                }}
                onCancel={() => setRenamingId(null)}
                className="px-1 py-0.5"
              />
            );
          }
          const selected = option.id === selectedId;
          // Saves on every pick and stays open until a click outside, the same as the tags picker.
          const pick = () => {
            if (!selected) onSelect(option.id);
          };
          return (
            <div
              key={option.id}
              className={clsx(
                'group flex min-h-8 cursor-pointer items-center gap-1 rounded-[4px] pr-1 pl-1.5 hover:bg-gray-150',
                selected && 'bg-gray-150',
              )}
              // The design-system Radio only wraps its circle in the <label>, so the name and the rest of the row pick the option here.
              onClick={(event) => {
                if (
                  event.target instanceof Element &&
                  event.target.closest('label, button')
                )
                  return;
                pick();
              }}
            >
              <Radio
                label={option.label}
                aria-label={option.label}
                checked={selected}
                onChange={pick}
                classes={{ root: 'min-w-0 flex-1 py-1.5' }}
              />
              <span className="flex gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                {onRenameOption && (
                  <IconButton
                    icon="Edit"
                    size="sm"
                    variant="minimal"
                    label={`${copy.rename} ${option.label}`}
                    title={copy.rename}
                    onClick={() => setRenamingId(option.id)}
                  />
                )}
                {onRemoveOption && (
                  <IconButton
                    icon="Trash"
                    size="sm"
                    variant="minimal"
                    label={`${copy.remove} ${option.label}`}
                    title={copy.remove}
                    onClick={() => onRemoveOption(option.id)}
                    className="hover:text-red-primary"
                  />
                )}
              </span>
            </div>
          );
        })}
      </div>

      {onAddOption &&
        (adding ? (
          <NameRow
            initial=""
            placeholder={copy.newOptionPlaceholder}
            copy={copy}
            taken={(label) => taken(label)}
            // The new option is selected, and the picker stays open showing it.
            onSave={(label) => {
              onAddOption(label);
              setAdding(false);
            }}
            onCancel={() => setAdding(false)}
            className="border-0 border-t border-solid border-gray-150 px-2 py-1.5"
          />
        ) : (
          <div className="flex items-center justify-between border-0 border-t border-solid border-gray-150 px-1 py-1">
            <Button
              variant="text"
              size="sm"
              prefixIcon="Plus"
              label={copy.addOption}
              onClick={() => setAdding(true)}
            />
            {selectedId && (
              <Button
                variant="text"
                size="sm"
                label={copy.clear}
                onClick={() => {
                  onClear();
                  close();
                }}
              />
            )}
          </div>
        ))}
    </Popover>
  );
};
